/**
 * Resolve the dataset references a saved query carries, and say what did not resolve.
 *
 * A saved query stores its datasets as a `[Datasets: ...]` prefix on the first
 * human message — a mix of UUIDs and names. ReportStepQuery resolved them twice,
 * with the same two defects in both copies:
 *
 *   - a UUID-shaped ref was pushed through on **shape alone**. The test was
 *     `/^[0-9a-f-]{36}$/`, which 36 dashes satisfy, and nothing checked the
 *     dataset still existed or was readable by this user. A deleted dataset
 *     silently became a selected one.
 *   - a name-shaped ref was matched against `datasets.find(...)` over the
 *     **first page only** — one request for `page_size: 100`, which is also the
 *     server's maximum (`Query(20, ge=1, le=100)` on `GET /api/v1/datasets`).
 *     Any dataset past the first hundred could not be found by name, and the
 *     failure was silent: the ref was dropped and the query loaded with fewer
 *     datasets than it was saved with, or none.
 *
 * Both are fixed here rather than in either copy, because the duplication is how
 * they came to differ in wording and agree in behaviour.
 *
 * **Unresolved refs are returned, not swallowed.** The old code dropped them, so
 * an author saw a saved query load with no datasets and no reason. What it can
 * no longer do is quietly proceed.
 */

/** Shape test only. Existence and permission are settled by the API, not a regex. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const looksLikeUuid = (ref) => UUID_RE.test(String(ref || '').trim());

/**
 * @param {string[]} refs  dataset references, UUIDs or names, as saved
 * @param {object} apiService  the API client (injected so this is testable without one)
 * @returns {Promise<{resolved: string[], unresolved: string[]}>}
 */
export async function resolveDatasetRefs(refs, apiService) {
  const resolved = [];
  const unresolved = [];

  for (const raw of refs) {
    const ref = String(raw || '').trim();
    if (!ref) continue;

    if (looksLikeUuid(ref)) {
      // Fetch it rather than trust it. This also settles permission: a dataset
      // the user may not read answers 403/404 here, which is the right outcome —
      // a saved query must not widen what its opener can see.
      try {
        const dataset = await apiService.getDataset(ref);
        if (dataset?.id) resolved.push(String(dataset.id));
        else unresolved.push(ref);
      } catch {
        unresolved.push(ref);
      }
      continue;
    }

    // A targeted search rather than a scan of whatever the picker happened to
    // load. Still exact-matched afterwards: `search` is a contains-match, so it
    // can return several, and picking a near-miss would silently point the alert
    // at the wrong data.
    try {
      const response = await apiService.getDatasets({ search: ref, page_size: 100 });
      const candidates = response?.items || response?.datasets || [];
      const found = candidates.find(
        (d) => (d.name || '').trim().toLowerCase() === ref.toLowerCase(),
      );
      if (found?.id) resolved.push(String(found.id));
      else unresolved.push(ref);
    } catch {
      unresolved.push(ref);
    }
  }

  return { resolved, unresolved };
}

/** Parse the `[Datasets: a, b]` prefix. Returns [] when there is none. */
export function parseDatasetPrefix(text) {
  const match = String(text || '').match(/^\[Datasets?:\s*([^\]]+)\]/i);
  if (!match) return [];
  return match[1]
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
